import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import type {WithChildren} from 'types/global';

/**
 * The props for the `HorizontalScroll` component.
 * Provides a full-bleed horizontally scrollable row with hidden scrollbar.
 */
interface ComponentProps {
    /** The gap between child items in grid-spacing units. Defaults to `3` (12px). */
    gap?: number;
    /**
     * The `testId` property represents a unique identifier assigned
     * to a component for testing purposes. It is applied as `data-cy`
     * attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'HorizontalScroll'
     */
    testId?: string;
}

/**
 * A horizontally scrollable container that bleeds to the viewport edges
 * while maintaining content padding. Hides the native scrollbar.
 *
 * @param props          The component props.
 * @param props.children The scrollable child elements.
 * @param props.gap      The gap between items in spacing units. Defaults to `3`.
 * @param props.testId   Test ID for Cypress selectors. Defaults to `'HorizontalScroll'`.
 * @returns A JSX element rendering the HorizontalScroll container.
 *
 * @example
 * ```tsx
 * <HorizontalScroll gap={6}>
 *     <TourCardHero ... />
 *     <TourCardHero ... />
 * </HorizontalScroll>
 * ```
 */
const HorizontalScroll = ({children, gap = 3, testId = 'HorizontalScroll'}: WithChildren<ComponentProps>) => (
    <Scroller $gap={gap} data-cy={testId}>
        {children}
    </Scroller>
);

HorizontalScroll.displayName = 'HorizontalScroll';

export {HorizontalScroll};

// ── Styled ──────────────────────────────────────────────────

interface ScrollerProps {
    /** Gap in spacing units. */
    $gap: number;
}

const Scroller = styled.div<ScrollerProps>`
    -ms-overflow-style: none;
    display: flex;
    gap: ${({$gap}) => spacing($gap)};
    margin: 0 -${spacing(6)};
    overflow-x: auto;
    padding: 0;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;